import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

// components
import Footer from 'src/components-v3/Footer';
import { Banner, UnidentifiedTraffic } from 'src/components/Feedback';
import { ContactRoutingJumpLinks } from 'src/components-v3/ContactRouting/ContactRoutingJumpLinks';
import { Unless } from 'src/components/helpers';
import { MetaTags } from 'src/components/MetaTags';
import { ConsentManager } from 'src/containers/ConsentManager';
import { CmsThemeContextProvider } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';

// helpers
import { AnalyticsProvider, LocaleContext } from 'src/contexts';
import { CmsThemes, Locales } from 'src/lib/constants';
import { getCurrentPage } from 'src/lib/utils';

// data
import SiteNavigation from 'src/components-v3/SiteNavigation/SiteNavigation';
import LegacyScripts from 'src/components/LegacyScripts';
import { noJSBannerData } from 'src/store/banners';

const ForeignContentNotification = dynamic(
  () => {
    return import(
      'src/components/Feedback/ForeignContentNotification/ForeignContentNotification.tsx'
    );
  },
  {
    ssr: false,
  },
);

// Returns css classes for page specific styles by route
const getClassForRoute = (route) => {
  // remove query params and hash
  const cleanRoute = route.split(/[?#]/)[0];
  const lastSegment = getCurrentPage(cleanRoute);

  // looks repetitive but we want a separation between route and css classes so that if
  // a page path changes we can update the switch case and leave the class name as is
  // (these class names are used across many scss modules)
  switch (lastSegment) {
    case 'how-we-handle-data':
      return 'how-we-handle-data';
    case 'how-it-works-for-consumers':
      return 'how-it-works-for-consumers';
    case 'what-is-plaid':
      return 'what-is-plaid';
    case 'why-is-plaid-involved':
      return 'why-is-plaid-involved';
    case 'discover-apps':
      return 'discover-apps';
    case 'open-banking':
      return 'open-banking';
    case 'pricing':
      return 'pricing';
    case 'contact-thanks':
      return 'contact-thanks';
    case 'contact':
      return 'contact';
    case 'account-funding':
      return 'account-funding';
    default:
      return 'homepage';
  }
};

// For legacy static pages
const Default = ({
  children,
  locale = Locales.EN_US,
  cmsTheme = CmsThemes.WEB2,
  chromeless = false,
  ...props
}) => {
  const { asPath: routeFromPath, query } = useRouter();
  const pageSpecificClass = chromeless ? '' : getClassForRoute(routeFromPath);

  return (
    <CmsThemeContextProvider cmsTheme={cmsTheme}>
      <LocaleContext.Provider value={locale}>
        <AnalyticsProvider>
          <MetaTags {...props} />
          <div className='banner-container'>
            <noscript>
              <Banner {...noJSBannerData} />
            </noscript>
          </div>

          <Unless condition={chromeless}>
            <SiteNavigation locale={locale} />
            <ForeignContentNotification hasFloatingNav />
          </Unless>

          <div
            id='main-content'
            className={`${pageSpecificClass} default ${query?.index}`}
          >
            {children}
          </div>
          <Unless condition={chromeless || (props && props['exclude-footer'])}>
            <Footer />
          </Unless>
          <ConsentManager />
          <Unless condition={chromeless}>
            <UnidentifiedTraffic />
            <ContactRoutingJumpLinks />
          </Unless>
          <LegacyScripts />
        </AnalyticsProvider>
      </LocaleContext.Provider>
    </CmsThemeContextProvider>
  );
};

export default Default;
